import React, { useState } from 'react';
import { UserAlert, deleteAlert, toggleAlert } from '../services/api';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from './ui/table';
import { Button } from './ui/button';
import { 
  Edit, 
  Trash2, 
  RefreshCw, 
  Bell, 
  BellOff,
  AlertTriangle,
  Calendar,
  Clock
} from 'lucide-react';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface AlertsListProps {
  alerts: UserAlert[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onEdit: (alert: UserAlert) => void;
}

const AlertsList: React.FC<AlertsListProps> = ({ 
  alerts, 
  loading, 
  error, 
  onRefresh,
  onEdit 
}) => {
  const [processingIds, setProcessingIds] = useState<Set<number>>(new Set());

  // Función para manejar la eliminación de una alerta
  const handleDelete = async (alertId: number) => {
    try {
      setProcessingIds(prev => new Set(prev).add(alertId));
      await deleteAlert(alertId);
      onRefresh(); // Recargar la lista después de eliminar
    } catch (err) {
      console.error('Error al eliminar la alerta:', err);
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(alertId);
        return newSet;
      });
    }
  };

  // Función para activar/desactivar una alerta
  const handleToggle = async (alertId: number) => {
    try {
      setProcessingIds(prev => new Set(prev).add(alertId));
      await toggleAlert(alertId);
      onRefresh(); // Recargar la lista después de cambiar el estado
    } catch (err) {
      console.error('Error al cambiar el estado de la alerta:', err);
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(alertId);
        return newSet;
      });
    }
  };

  // Función para formatear la fecha
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd MMM yyyy, HH:mm', { locale: es });
    } catch (e) {
      return dateString;
    }
  };

  // Renderizar un mensaje de error si hay uno
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <AlertTriangle className="h-12 w-12 text-red-500 mb-4" />
        <p className="text-lg font-medium mb-2">Error al cargar las alertas</p>
        <p className="text-gray-500 mb-4">{error}</p>
        <Button onClick={onRefresh} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Intentar de nuevo
        </Button>
      </div>
    );
  }

  // Renderizar un esqueleto de carga si está cargando
  if (loading) {
    return (
      <div className="w-full">
        <div className="flex justify-end mb-4">
          <Skeleton className="h-10 w-24" />
        </div>
        <div className="border rounded-md">
          <div className="p-4">
            <Skeleton className="h-8 w-full mb-4" />
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-16 w-full mb-2" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Renderizar un mensaje si no hay alertas
  if (alerts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <Bell className="h-12 w-12 text-gray-400 mb-4" />
        <p className="text-lg font-medium mb-2">No tienes alertas configuradas</p>
        <p className="text-gray-500 mb-4">Crea una nueva alerta para recibir notificaciones sobre documentos del BOE</p>
      </div>
    );
  }

  // Renderizar la tabla de alertas
  return (
    <div className="w-full">
      <div className="flex justify-end mb-4">
        <Button onClick={onRefresh} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualizar
        </Button>
      </div>
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Filtros</TableHead>
              <TableHead>Frecuencia</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Creada</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {alerts.map((alert) => (
              <TableRow key={alert.id}>
                <TableCell className="font-medium">{alert.alert_name}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {alert.seccion_codigo && (
                      <Badge variant="outline" className="text-xs">
                        {alert.seccion_nombre || alert.seccion_codigo}
                      </Badge>
                    )}
                    {alert.departamento_codigo && (
                      <Badge variant="outline" className="text-xs">
                        {alert.departamento_nombre || alert.departamento_codigo}
                      </Badge>
                    )}
                    {alert.tipo_documento && (
                      <Badge variant="outline" className="text-xs">
                        {alert.tipo_documento}
                      </Badge>
                    )}
                    {alert.busqueda && (
                      <Badge variant="outline" className="text-xs">
                        Búsqueda: {alert.busqueda}
                      </Badge>
                    )}
                    {alert.texto && (
                      <Badge variant="outline" className="text-xs">
                        Texto: {alert.texto}
                      </Badge>
                    )}
                    {alert.fecha_desde && (
                      <Badge variant="outline" className="text-xs">
                        Desde: {alert.fecha_desde}
                      </Badge>
                    )}
                    {alert.fecha_hasta && (
                      <Badge variant="outline" className="text-xs">
                        Hasta: {alert.fecha_hasta}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span className="capitalize">
                      {alert.frequency === 'daily' ? 'Diaria' : 
                       alert.frequency === 'weekly' ? 'Semanal' : 
                       alert.frequency === 'monthly' ? 'Mensual' : 
                       alert.frequency}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge 
                    variant={alert.is_active ? "default" : "secondary"}
                    className="flex items-center gap-1"
                  >
                    {alert.is_active ? 
                      <><Bell className="h-3 w-3" /> Activa</> : 
                      <><BellOff className="h-3 w-3" /> Inactiva</>
                    }
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    <span>{formatDate(alert.created_at)}</span>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleToggle(alert.id!)}
                      disabled={processingIds.has(alert.id!)}
                    >
                      {alert.is_active ? 
                        <BellOff className="h-4 w-4" /> : 
                        <Bell className="h-4 w-4" />
                      }
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => onEdit(alert)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta acción eliminará permanentemente la alerta "{alert.alert_name}".
                            Esta acción no se puede deshacer.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDelete(alert.id!)}
                            className="bg-red-500 hover:bg-red-700"
                            disabled={processingIds.has(alert.id!)}
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AlertsList; 